import { Dispatch } from "redux"
import { IBillabilityQuery, IBillabilityRow, ITrendPoint, ITrendGroupPoint } from "models/billability"
import { BillabilityReportRecipient } from "models/billability-report-recipient"
import Employee from "models/employee"
import { setBillabilitySearchFields } from "./reducer"
import { toast } from "react-toastify"
import { buildQueryString } from "utils/query-string"
import Department from "models/department"
import Branch from "models/branch"

export const downloadBillabilityCSV = (
    rows: IBillabilityRow[],
    query: IBillabilityQuery,
    departments: Department[],
    branches: Branch[],
    // Resolved display label for the grouping -- the query only carries
    // 'departmentLevel' plus a level id, neither of which means anything in an export.
    groupByLabel: string,
) => {
    const fmt = (v: number) => v.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

    const namesOrAll = (ids: string[] | undefined, all: { id: string, label: string }[]) =>
        ids && ids.length > 0 ? all.filter(a => ids.includes(a.id)).map(a => a.label).join(', ') : 'All'

    const metadata = [
        `From: ${query.fromDate}`,
        `To: ${query.toDate}`,
        `Department: ${namesOrAll(query.departmentIds, departments.map(d => ({ id: d.id, label: d.description })))}`,
        `Branch: ${namesOrAll(query.branchIds, branches.map(b => ({ id: b.id, label: b.name })))}`,
        `Employee Class: ${query.employeeClassIds && query.employeeClassIds.length > 0 ? query.employeeClassIds.join(', ') : 'All'}`,
        `Group By: ${groupByLabel}`,
    ].join(' | ')

    const header = [
        'Name', 'Department', 'Branch', 'Utilization (%)', 'Billable (%)',
        'Time Available (Hr)', 'Bill Rate (%)', 'Billable Target (Hr)',
        'Time Total (Hr)', 'Time Billable (Hr)', 'RAM Time Non Billable (Hr)',
        'Prospects Time Non Billable (Hr)', 'Non Project Time Non Billable (Hr)',
        'Other Non Billable (Hr)',
    ]

    const toRow = (r: IBillabilityRow): string[] => [
        r.employeeName, r.departmentName, r.branchName,
        fmt(r.utilizationPct), fmt(r.actualBillablePct),
        fmt(r.timeAvailable), fmt(r.billRatePct), fmt(r.billableTarget),
        fmt(r.timeTotal), fmt(r.timeBillable), fmt(r.ramTimeNonBillable),
        fmt(r.prospectsTimeNonBillable),
        fmt(r.projxTimeNonBillable - r.statTimeNonBillable),
        fmt(r.otherNonBillable),
    ]

    // rows arrive already flattened in the order the table renders them, so the CSV
    // carries the same sort and grouping the user is looking at.
    const csvRows: string[][] = [[metadata], [], header, ...rows.map(toRow)]

    const csv = csvRows
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `billability-${query.fromDate}-${query.toDate}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

export const getAllBillability = (query: IBillabilityQuery) => async (dispatch: Dispatch): Promise<{ data: IBillabilityRow[], trend: ITrendPoint[], trendByGroup: ITrendGroupPoint[] } | undefined> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability${buildQueryString(query)}`

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message)
        }

        const data = await res.json()
        return { data: data.data, trend: data.trend, trendByGroup: data.trendByGroup }
    } catch (err: any) {
        toast.error(err.message ?? "Error loading billability report")
        return undefined
    }
}


export const syncBillabilityReport = async (year: string): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/sync`

        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ year }),
            headers: { 'Content-Type': 'application/json' }
        })

        if (!res.ok) {
            const errBody = await res.json()
            throw new Error(errBody.message)
        }

        toast.success("Sync started. The page will refresh automatically when it finishes.")
        return true
    } catch (err: any) {
        toast.error(err.message ?? "Error syncing billability report")
        return false
    }
}


export const getBillabilitySearchFields = () => async (dispatch: Dispatch) => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/search-fields`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        dispatch(setBillabilitySearchFields(data.data))
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
    }
}

// ── Report email recipients (admin-only modal) ──────────────────────────────

export const getBillabilityReportRecipients = async (): Promise<BillabilityReportRecipient[] | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/report-recipients`;
        const res = await fetch(url, { credentials: 'include', method: 'GET' })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error getting report recipients");
        return null
    }
}

export const getBillabilityReportRecipientFormFields = async (): Promise<{ employees: Partial<Employee>[] } | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/report-recipients/form-fields`;
        const res = await fetch(url, { credentials: 'include', method: 'GET' })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error getting form fields");
        return null
    }
}

export const createBillabilityReportRecipient = async (employeeId: string, weekly: boolean, monthly: boolean): Promise<BillabilityReportRecipient | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/report-recipients`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST',
            body: JSON.stringify({ employeeId, weekly, monthly }),
            headers: { 'Content-Type': 'application/json' },
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Recipient added.");
        const data = await res.json();
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error adding recipient");
        return null
    }
}

export const updateBillabilityReportRecipient = async (id: string, weekly: boolean, monthly: boolean): Promise<BillabilityReportRecipient | null> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/report-recipients/${id}`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'PATCH',
            body: JSON.stringify({ weekly, monthly }),
            headers: { 'Content-Type': 'application/json' },
        })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Error updating recipient");
        return null
    }
}

export const deleteBillabilityReportRecipient = async (id: string): Promise<boolean> => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/billability/report-recipients/${id}`;
        const res = await fetch(url, { credentials: 'include', method: 'DELETE' })

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        toast.success("Recipient removed.");
        return true
    } catch (err: any) {
        toast.error(err.message ?? "Error removing recipient");
        return false
    }
}